import { Core } from "@faire/design-tokens";
import { IProduct } from "@faire/web-api/indigofair/data/IProduct";
import { IProductVariation } from "@faire/web-api/indigofair/data/IProductVariation";
import { IProductVariationMap } from "@faire/web-api/indigofair/data/IProductVariationMap";
import {
  localNum,
  useStrictLocalization,
} from "@faire/web/common/localization/localize";
import { Column, Row } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Span, Typography } from "@faire/web/slate/Typography";
import { useViewport } from "@faire/web/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { observer } from "mobx-react";
import * as React from "react";
import { useMemo, useState } from "react";
import styled from "styled-components";

import { Chevron } from "@faire/retailer/icons/Chevron/Chevron";
import { titleCaseWithSpecialCharacters } from "@faire/retailer/lib/string";
import { OptionsRow } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/__internal__/SpecialOptionPicker/OptionsRow";
import { OptionVariation } from "@faire/retailer/views/product/ProductDetails/AddToCart/OptionSelection/__internal__/SpecialOptionPicker/OptionVariation";

import { getVariantDescriptorSuffix } from "./getVariantDescriptorSuffix";
import { SizeChartLink } from "./SizeChartLink";
import { VariantOptionSelectionViewState } from "./VariantOptionSelectionViewState";
import { IProductPageResponse } from "@faire/web-api/indigofair/data/IProductPageResponse";

// Show two rows of swatches at all times
export const NUM_ROWS_OF_SWATCHES = 2;
export const SWATCH_WIDTH = 48;
// Height of a single swatch in px
// Allows for animating showing more/less swatches
const SWATCH_ROW_TABLET_WIDTH = 300;
const SWATCH_ROW_DEFAULT_WIDTH = 360;

export interface SpecialOptionPickerProps {
  variationName: string;
  variantOptionSelectionViewState: VariantOptionSelectionViewState;
  variationDisplayType: keyof typeof IProductVariationMap.IEntry.VariationDisplayType;
  alt?: string;
  brand: IProductPageResponse.IPdpBrand;
}

const DEFAULT_VARIATION_NAME = "default";

const TITLE_CASE_WITH_SPECIAL_CHARACTERS =
  titleCaseWithSpecialCharacters("Size");

const SpecialOptionPickerFn: React.FC<SpecialOptionPickerProps> = ({
  variantOptionSelectionViewState,
  variationName,
  variationDisplayType,
  alt,
  brand,
}) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>(
    undefined
  );

  const { strictLocalize } = useStrictLocalization();

  const [showAllSwatches, setShowAllSwatches] = useState<boolean>(false);
  const { isTablet } = useViewport();

  const variationOptions =
    variantOptionSelectionViewState.variationsGroupedByName[variationName];

  const selectedVariationValue =
    variantOptionSelectionViewState.getSelectedVariationValue(variationName);

  const variationDisplayValue = hoveredValue || selectedVariationValue;

  const numSwatchesPerRow = Math.floor(
    (isTablet ? SWATCH_ROW_TABLET_WIDTH : SWATCH_ROW_DEFAULT_WIDTH) /
      SWATCH_WIDTH
  );

  const totalNumRowsOfSwatches = Math.ceil(
    (variationOptions?.length ?? 0) / numSwatchesPerRow
  );

  const isSwatchPicker =
    variationDisplayType ===
    IProductVariationMap.IEntry.VariationDisplayType.SWATCH;

  const showExpandButton =
    isSwatchPicker &&
    (variationOptions?.length ?? 0) > numSwatchesPerRow * NUM_ROWS_OF_SWATCHES;

  const displayableName =
    !variationName || variationName.toLowerCase() === DEFAULT_VARIATION_NAME
      ? strictLocalize({
          defaultMessage: "Option",
          description: { text: "A single product option." },
        })
      : titleCaseWithSpecialCharacters(variationName);

  const dropdownText = showAllSwatches ? (
    <StrictLocalMsg
      defaultMessage="Fewer colors"
      description={{ text: "Button to hide excess swatch options" }}
    />
  ) : (
    <StrictLocalMsg
      defaultMessage="{AdditionalSwatchOptionsNumber} more colors"
      description={{
        text: "Button to show more swatch options",
      }}
      values={{
        AdditionalSwatchOptionsNumber: localNum(
          (variationOptions?.length ?? 0) - numSwatchesPerRow * 2
        ),
      }}
    />
  );

  const selectMessage = useMemo(() => {
    if (hoveredValue === undefined) {
      return null;
    }

    const variation = IProductVariation.build({
      name: variationName,
      value: hoveredValue,
    });

    const variationState =
      variantOptionSelectionViewState.stateForAlternateVariation(variation);

    const option =
      variantOptionSelectionViewState.optionForAlternateVariation(variation);

    // this method gets the HOVER message - i.e. if I'm hovering over an option
    // and it should be OOS, it should show OOS. For open sizing, we cannot simply
    // check for the single product option but the entire size variant to see
    // if it is purchasable (available/in stock)
    if (
      (variantOptionSelectionViewState.isAlternateOpenSizingProduct(option) &&
        variantOptionSelectionViewState.isAlternateOpenSizingAvailable(
          option
        )) ||
      variationState === IProduct.State.SALES_PAUSED
    ) {
      return null;
    }

    return getVariantDescriptorSuffix(variationState, strictLocalize) ?? null;
  }, [
    hoveredValue,
    variantOptionSelectionViewState,
    variationName,
    strictLocalize,
  ]);
  const showSizeChartLink =
    !!brand.brand_size_chart &&
    displayableName === TITLE_CASE_WITH_SPECIAL_CHARACTERS;

  const options = useMemo(() => {
    return variationOptions
      ?.filter((variation) => variation.value !== undefined)
      .map((variation, index) => (
        <OptionVariation
          key={index}
          productVariation={variation}
          index={index}
          viewState={variantOptionSelectionViewState}
          variationOptions={variationOptions}
          variationDisplayType={variationDisplayType}
          variationName={variationName ?? ""}
          hoveredValue={hoveredValue}
          setHoveredValue={(value) => setHoveredValue(value ?? undefined)}
          alt={alt ?? ""}
          showAllSwatches={showAllSwatches}
        />
      ));
  }, [
    alt,
    hoveredValue,
    showAllSwatches,
    variationDisplayType,
    variationName,
    variationOptions,
    variantOptionSelectionViewState,
  ]);

  return (
    <FullColumn>
      <Row justify="space-between">
        <Row style={{ width: "100%" }}>
          <Typography truncate>{displayableName}</Typography>
          <Spacer size={4} />
          <Typography>
            {variationDisplayValue}{" "}
            <Span
              color={Core.text.critical}
              style={{ whiteSpace: "nowrap" }}
              data-test-id="span-sop"
            >
              {selectMessage}
            </Span>
          </Typography>
        </Row>
        {showSizeChartLink ? (
          <Row>
            <SizeChartLink
              brand={brand}
              product={variantOptionSelectionViewState.product}
            />
          </Row>
        ) : null}
      </Row>
      <OptionsRow
        isSwatchPicker={isSwatchPicker}
        shouldShowAllSwatches={showAllSwatches}
        totalNumRowsOfSwatches={totalNumRowsOfSwatches}
        options={options}
      />
      {showExpandButton ? (
        <DropdownButton onClick={() => setShowAllSwatches((prev) => !prev)}>
          <Typography>
            {dropdownText}{" "}
            <Chevron direction={showAllSwatches ? "up" : "down"} />
          </Typography>
        </DropdownButton>
      ) : null}
      <Spacer size="3x" />
    </FullColumn>
  );
};

export const SpecialOptionPicker = observer(SpecialOptionPickerFn);

const DropdownButton = styled.button`
  padding: ${getSpacing("1x", 0)};
  text-align: left;
`;

const FullColumn = styled(Column)`
  width: 100%;
`;
